import { Box, Divider, Grid, styled, Typography } from '@quillbot/design-system';
import { Add } from '@mui/icons-material';
import React, { useRef, useState } from 'react';
import { FullTextCitationEduOverflow } from './FullTextCitationEdu';

const fontProps = {
  component: 'p',
  fontSize: '14px',
  fontWeight: 400,
};
const CitationStyleHeadingAndDetails = [
  {
    heading: 'APA Citations',
    details: [
      'APA stands for the American Psychological Association, and APA format is most often used in the social and behavioral sciences, which include fields like psychology, education, business, anthropology, economics, and geography.',
      'In APA format, full citations are listed on a References page, rather than a Works Cited or Bibliography page.',
    ],
    imgSrc: '/images/coWriter/info-apa.svg',
    example: (
      <>
        <b>APA Example:&nbsp;</b>
        <span>Salinger, J. D. (1951).&nbsp;</span>
        <i>The catcher in the rye.</i>
        <span>&nbsp;Little, Brown and Company.</span>
      </>
    ),
  },
  {
    heading: 'MLA Citations',
    details: [
      'MLA stands for the Modern Language Association and is mostly used in the humanities, such as history, languages, literature, philosophy, and the arts. The MLA citation style was originally used in literature and language studies but was quickly adopted by other humanities because of the focus on specific source notation, like page and paragraph numbers.',
      'Full citations in MLA format are always listed on a Works Cited page.',
    ],
    imgSrc: '/images/coWriter/info-mla.svg',
    example: (
      <>
        <b>MLA Example:&nbsp;</b>
        <span>Corey, James S. A.&nbsp;</span>
        <i>Leviathan Wakes.</i>
        <span>&nbsp;Orbit US, 2011.</span>
      </>
    ),
  },
  {
    heading: 'Chicago Style Citation',
    details: [
      'Chicago style citations, in the Author-Date system which QuillBot supports, are used in the natural and physical sciences, social sciences, math, as well as for manuscript editing and publication.',
      'The Chicago style Author-Date system uses either a References or Works Cited page to list full citation entries for source material. A Bibliography page is only used in the Notes and Bibliography Chicago style system, so don’t be fooled if you’re working in Author-Date!',
    ],
    imgSrc: '/images/coWriter/info-chicago.svg',
    example: (
      <>
        <b>Chicago Example:&nbsp;</b>
        <span>Corey, James S. A. 2011.&nbsp;</span>
        <i>Leviathan Wakes.</i>
        <span>&nbsp;New York: Orbit US.</span>
      </>
    ),
  },
];

const StyledGrid = styled(Grid)`
  & .accordian-expand-icon {
    font-size: 16px;
    color: ${({ theme }) => theme.specialColors.ICON_DEFAULT_COLOR};
  }

  &:hover {
    & .accordian-expand-icon {
      color: ${({ theme }) => theme.palette.primary.main};
    }
    & .accordian-heading {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

interface CitationAccordianProps {
  heading: string;
  details: string[];
  example: JSX.Element;
  onChange: () => void;
  isExpanded: boolean;
}

const CitationAccordian = (props: CitationAccordianProps) => {
  const { heading, details, onChange, isExpanded } = props;
  const detailsPriviewRef = useRef<HTMLDivElement>(null);
  const height = isExpanded ? detailsPriviewRef.current?.scrollHeight : 24;

  return (
    <StyledGrid container onClick={onChange}>
      <Grid container item>
        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          style={{ cursor: 'pointer' }}
        >
          <Grid item xs={11}>
            <Typography
              component="h3"
              fontSize="20px"
              fontWeight="600"
              sx={[isExpanded && { color: 'primary.main' }]}
              className="accordian-heading"
            >
              {heading}
            </Typography>
          </Grid>

          <Grid item xs={1} textAlign="end">
            {!isExpanded && <Add className="accordian-expand-icon" />}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        style={{
          height,
          overflow: isExpanded ? 'unset' : 'hidden',
          wordBreak: isExpanded ? 'break-word' : 'break-all',
          cursor: 'pointer',
          marginTop: 10,
          transition: 'all 0.3s',
        }}
      >
        <div
          ref={detailsPriviewRef}
          style={{
            height,
            overflow: 'hidden',
            maxWidth: isExpanded ? 'unset' : 'calc(100% - 15px)',
            transition: 'all 0.3s',
            cursor: 'pointer',
          }}
        >
          {details.map((detail, index) => (
            <Typography key={detail.slice(0, 15)} pt={index === 0 ? 0 : '6px'} {...fontProps}>
              {detail}
            </Typography>
          ))}

          <Typography pt="20px" {...fontProps}>
            {props.example}
          </Typography>
        </div>

        <span style={{ marginTop: '2px' }}>{isExpanded ? '' : '...'}</span>
      </Grid>
    </StyledGrid>
  );
};

const Accordian = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);

  return (
    <Box maxWidth="940px" mt="48px">
      <Grid container>
        <Grid item justifyItems="center" alignItems="center" textAlign="center" xs={12} sm={3}>
          <img
            src={CitationStyleHeadingAndDetails[imgIdx].imgSrc}
            alt={CitationStyleHeadingAndDetails[imgIdx].heading}
            style={{ marginBottom: '16px', width: '100%' }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={9}
          sx={{
            pl: {
              xs: 0,
              sm: '40px',
            },
          }}
        >
          {CitationStyleHeadingAndDetails.map((headingAndDetails, index) => (
            <>
              <CitationAccordian
                key={headingAndDetails.heading}
                heading={headingAndDetails.heading}
                details={headingAndDetails.details}
                example={headingAndDetails.example}
                isExpanded={index === activeIdx}
                onChange={() => {
                  setActiveIdx((prevIdx) => (prevIdx === index ? -1 : index));
                  setImgIdx(index);
                }}
              />

              {index !== CitationStyleHeadingAndDetails.length - 1 && (
                <Divider style={{ margin: '10px 0px' }} />
              )}
            </>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

const FormatsAndExample = () => (
  <Grid
    container
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    // position="relative"
    px="16px"
    mb="48px"
  >
    <Typography
      component="h2"
      p="48px 16px 24px"
      fontSize="30px"
      fontWeight={400}
      textAlign="center"
    >
      Formats Supported by QuillBot’s Citation Generator
    </Typography>

    <Typography component="p" fontSize="16px" fontWeight={400} textAlign="center" maxWidth="636px">
      QuillBot can help you create accurate citations in APA, MLA, and Chicago style citation
      formats.
    </Typography>

    <Accordian />

    <FullTextCitationEduOverflow />
  </Grid>
);

export default FormatsAndExample;
